import { useEffect, useMemo, useState } from 'react';
import { fhirClient } from 'src/App';
import { getReferenceResourceId, getReferenceResourceType } from 'src/careflow/helpers/common';
import { WrappedServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { Bundle, FhirResource, HealthcareService, Organization, Practitioner, PractitionerRole } from 'src/careflow/ph-core/fhir-client/resources';
import {
  dedupeIcomxContactCandidates,
  type IcomxContactCandidate,
} from './icomxContactCandidateModel';
import {
  buildIcomxCandidateFromResource,
} from './contactCandidates';

interface UseIcomxContactCandidatesResult {
  candidates: IcomxContactCandidate[];
  isLoading: boolean;
}

const resourceCache = new Map<string, FhirResource | null>();
const resourcePromiseCache = new Map<string, Promise<FhirResource | null>>();
const practitionerRoleSearchCache = new Map<string, PractitionerRole[]>();
const practitionerRoleSearchPromiseCache = new Map<string, Promise<PractitionerRole[]>>();

function getCachedResource(reference: string | null | undefined) {
  if (!reference || !resourceCache.has(reference)) {
    return undefined;
  }

  return resourceCache.get(reference);
}

async function prefetchResourcesByReferences(references: string[]) {
  const missingReferences = Array.from(
    new Set(
      references.filter(
        (reference): reference is string =>
          !!reference && getCachedResource(reference) === undefined && !resourcePromiseCache.has(reference)
      )
    )
  );

  if (!missingReferences.length) {
    return;
  }

  const referencesByResourceType = missingReferences.reduce<Record<string, string[]>>(
    (groupedReferences, reference) => {
      const resourceType = getReferenceResourceType(reference);
      const resourceId = getReferenceResourceId(reference);

      if (!resourceType || !resourceId) {
        return groupedReferences;
      }

      return {
        ...groupedReferences,
        [resourceType]: [...(groupedReferences[resourceType] ?? []), resourceId],
      };
    },
    {}
  );

  await Promise.all(
    Object.entries(referencesByResourceType).map(async ([resourceType, resourceIds]) => {
      try {
        const bundle = (await fhirClient.search(
          `${resourceType}?_id=${resourceIds.join(',')}`
        )) as Bundle<FhirResource>;
        const fetchedResources =
          bundle.entry
            ?.map((entry) => entry.resource)
            .filter((resource): resource is FhirResource => !!resource) ?? [];
        const fetchedIds = new Set<string>();

        fetchedResources.forEach((resource) => {
          if (!resource.id) {
            return;
          }

          fetchedIds.add(resource.id);
          resourceCache.set(`${resource.resourceType}/${resource.id}`, resource);
        });

        resourceIds.forEach((resourceId) => {
          const reference = `${resourceType}/${resourceId}`;

          if (!fetchedIds.has(resourceId)) {
            resourceCache.set(reference, null);
          }
        });
      } catch (error) {
        await Promise.all(
          resourceIds.map((resourceId) => fetchResourceByReference(`${resourceType}/${resourceId}`))
        );
      }
    })
  );
}

async function fetchResourceByReference(reference: string | null | undefined): Promise<FhirResource | null> {
  if (!reference) {
    return null;
  }

  const cachedResource = getCachedResource(reference);

  if (cachedResource !== undefined) {
    return cachedResource;
  }

  const pendingResource = resourcePromiseCache.get(reference);

  if (pendingResource) {
    return pendingResource;
  }

  const resourcePromise: Promise<FhirResource | null> = fhirClient
    .getResource(reference)
    .then((resource) => {
      const typedResource = resource as FhirResource;
      resourceCache.set(reference, typedResource);
      resourcePromiseCache.delete(reference);
      return typedResource;
    })
    .catch(() => {
      resourceCache.set(reference, null);
      resourcePromiseCache.delete(reference);
      return null;
    });

  resourcePromiseCache.set(reference, resourcePromise);

  return resourcePromise;
}

async function fetchResourcesByReferences(references: string[]) {
  const resources = await Promise.all(references.map((reference) => fetchResourceByReference(reference)));
  return resources.filter((resource): resource is FhirResource => !!resource);
}

async function fetchPractitionerRolesByHealthcareServiceIds(ids: string[]) {
  if (!ids.length) {
    return [] as PractitionerRole[];
  }

  const cacheKey = ids.filter(Boolean).sort().join(',');

  if (practitionerRoleSearchCache.has(cacheKey)) {
    return practitionerRoleSearchCache.get(cacheKey) ?? [];
  }

  const pendingRoles = practitionerRoleSearchPromiseCache.get(cacheKey);

  if (pendingRoles) {
    return pendingRoles;
  }

  try {
    const rolesPromise = fhirClient
      .search(`PractitionerRole?healthcare-service=${ids.join(',')}`)
      .then((bundle) => {
        const resources =
          (bundle as Bundle<PractitionerRole>).entry
            ?.map((entry) => entry.resource)
            .filter((resource): resource is PractitionerRole => !!resource) ?? [];

        practitionerRoleSearchCache.set(cacheKey, resources);
        practitionerRoleSearchPromiseCache.delete(cacheKey);

        resources.forEach((resource) => {
          if (resource.id) {
            resourceCache.set(`PractitionerRole/${resource.id}`, resource);
          }
        });

        return resources;
      })
      .catch(() => {
        practitionerRoleSearchCache.set(cacheKey, []);
        practitionerRoleSearchPromiseCache.delete(cacheKey);
        return [] as PractitionerRole[];
      });

    practitionerRoleSearchPromiseCache.set(cacheKey, rolesPromise);

    return await rolesPromise;
  } catch (error) {
    return [];
  }
}

function getRelatedResource<TResource extends FhirResource>(
  resources: FhirResource[],
  resourceType: TResource['resourceType'],
  id: string | undefined
) {
  if (!id) {
    return null;
  }

  return resources.find(
    (resource): resource is TResource => resource.resourceType === resourceType && resource.id === id
  ) ?? null;
}

function buildCandidatesForPractitionerRole(
  practitionerRole: PractitionerRole,
  relatedResources: FhirResource[],
  sourceLabel: string
) {
  const practitioner = getRelatedResource<Practitioner>(
    relatedResources,
    'Practitioner',
    getReferenceResourceId(practitionerRole.practitioner?.reference ?? '')
  );
  const organization = getRelatedResource<Organization>(
    relatedResources,
    'Organization',
    getReferenceResourceId(practitionerRole.organization?.reference ?? '')
  );

  const candidates = [
    buildIcomxCandidateFromResource(practitionerRole, {
      sourceLabel,
      practitionerRole: {
        practitioner,
        organization,
      },
    }),
    practitioner
      ? buildIcomxCandidateFromResource(practitioner, {
          sourceLabel: `${sourceLabel} Practitioner`,
        })
      : null,
    organization
      ? buildIcomxCandidateFromResource(organization, {
          sourceLabel: `${sourceLabel} Organization`,
        })
      : null,
  ];

  return candidates.filter((candidate): candidate is IcomxContactCandidate => !!candidate);
}

export async function prefetchIcomxReferences(
  references: Array<string | null | undefined>
) {
  const stableReferences = references.filter((reference): reference is string => !!reference);

  if (!stableReferences.length) {
    return;
  }

  await prefetchResourcesByReferences(stableReferences);

  const directResources = await fetchResourcesByReferences(stableReferences);
  const practitionerRoles = directResources.filter(
    (resource): resource is PractitionerRole => resource.resourceType === 'PractitionerRole'
  );

  if (!practitionerRoles.length) {
    return;
  }

  const linkedReferences = practitionerRoles.flatMap((role) =>
      [role.practitioner?.reference, role.organization?.reference].filter(
        (reference): reference is string => !!reference
      )
    );

  await prefetchResourcesByReferences(linkedReferences);
}

export function useTaskOwnerIcomxCandidates(ownerReference: string | undefined | null): UseIcomxContactCandidatesResult {
  return useReferenceIcomxCandidates(ownerReference, 'Task Owner');
}

export function useReferenceIcomxCandidates(
  reference: string | undefined | null,
  sourceLabel?: string
): UseIcomxContactCandidatesResult {
  const [candidates, setCandidates] = useState<IcomxContactCandidate[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadCandidates = async () => {
      if (!reference) {
        if (isMounted) {
          setCandidates([]);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);

      const ownerResource = await fetchResourceByReference(reference);
      const ownerType = getReferenceResourceType(reference);
      let nextCandidates: IcomxContactCandidate[] = [];

      if (ownerResource?.resourceType === 'PractitionerRole') {
        const relatedResources = await fetchResourcesByReferences(
          [ownerResource.practitioner?.reference, ownerResource.organization?.reference].filter(
            (reference): reference is string => !!reference
          )
        );
        nextCandidates = buildCandidatesForPractitionerRole(
          ownerResource,
          relatedResources,
          sourceLabel ?? 'Task Owner'
        );
      } else {
        const candidate = buildIcomxCandidateFromResource(ownerResource, {
          sourceLabel: ownerType ? sourceLabel : undefined,
        });
        nextCandidates = candidate ? [candidate] : [];
      }

      if (isMounted) {
        setCandidates(dedupeIcomxContactCandidates(nextCandidates));
        setIsLoading(false);
      }
    };

    void loadCandidates();

    return () => {
      isMounted = false;
    };
  }, [reference, sourceLabel]);

  return { candidates, isLoading };
}

export function useIcomxResourceCandidates(
  resourceType: string | undefined,
  resourceId: string | undefined
): UseIcomxContactCandidatesResult {
  const reference = resourceType && resourceId ? `${resourceType}/${resourceId}` : null;

  return useReferenceIcomxCandidates(reference);
}

export function useServiceRequestPerformerIcomxCandidatesFromReferences(
  performerReferences: string[]
): UseIcomxContactCandidatesResult {
  const [candidates, setCandidates] = useState<IcomxContactCandidate[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const stablePerformerReferences = useMemo(
    () => performerReferences.filter((reference): reference is string => !!reference),
    [performerReferences]
  );

  useEffect(() => {
    let isMounted = true;

    const loadCandidates = async () => {
      if (!stablePerformerReferences.length) {
        if (isMounted) {
          setCandidates([]);
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);

      const directResources = await fetchResourcesByReferences(stablePerformerReferences);
      const healthcareServices = directResources.filter(
        (resource): resource is HealthcareService => resource.resourceType === 'HealthcareService'
      );
      const directPractitionerRoles = directResources.filter(
        (resource): resource is PractitionerRole => resource.resourceType === 'PractitionerRole'
      );

      const healthcareServiceRoleResources = await fetchPractitionerRolesByHealthcareServiceIds(
        healthcareServices.map(({ id }) => id ?? '').filter(Boolean)
      );

      const roleResources = [...directPractitionerRoles, ...healthcareServiceRoleResources];
      const roleLinkedReferences = roleResources.flatMap((role) =>
        [role.practitioner?.reference, role.organization?.reference].filter(
          (reference): reference is string => !!reference
        )
      );
      const healthcareServiceLinkedReferences = healthcareServices
        .map((service) => service.providedBy?.reference)
        .filter((reference): reference is string => !!reference);
      const relatedResources = await fetchResourcesByReferences([
        ...roleLinkedReferences,
        ...healthcareServiceLinkedReferences,
      ]);

      const nextCandidates = [
        ...directResources.map((resource) =>
          buildIcomxCandidateFromResource(resource, {
            sourceLabel: resource.resourceType === 'HealthcareService' ? 'Service Request Performer' : undefined,
          })
        ),
        ...roleResources.flatMap((role) =>
          buildCandidatesForPractitionerRole(
            role,
            relatedResources,
            directPractitionerRoles.some(({ id }) => id === role.id)
              ? 'Service Request Performer'
              : 'Healthcare Service Related Contact'
          )
        ),
        ...healthcareServices.flatMap((service) => {
          const providedByOrganization = getRelatedResource<Organization>(
            relatedResources,
            'Organization',
            getReferenceResourceId(service.providedBy?.reference ?? '')
          );

          return providedByOrganization
            ? [
                buildIcomxCandidateFromResource(providedByOrganization, {
                  sourceLabel: 'Healthcare Service Organization',
                }),
              ]
            : [];
        }),
      ].filter((candidate): candidate is IcomxContactCandidate => !!candidate);

      if (isMounted) {
        setCandidates(dedupeIcomxContactCandidates(nextCandidates));
        setIsLoading(false);
      }
    };

    void loadCandidates();

    return () => {
      isMounted = false;
    };
  }, [stablePerformerReferences]);

  return { candidates, isLoading };
}

export function useServiceRequestPerformerIcomxCandidates(
  serviceRequest: WrappedServiceRequest | null | undefined
): UseIcomxContactCandidatesResult {
  const performerReferences = useMemo(
    () =>
      serviceRequest?.performer
        ?.map(({ reference }) => reference)
        .filter((reference): reference is string => !!reference) ?? [],
    [serviceRequest]
  );

  return useServiceRequestPerformerIcomxCandidatesFromReferences(performerReferences);
}
